<script setup lang="ts">
import KartNavigation from "../../../components/website/KartNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { useOwner } from "../../../stores/website/owner";
import PackCard from "../../../components/website/PackCard.vue";
import type { OptionCardProps } from "../../../components/website/OptionCard.vue";
import OptionCard from "../../../components/website/OptionCard.vue";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isSmallerThanXl = breakpoints.smallerOrEqual("xl");

const { setTranslateNamespace, translate } = useTranslate();
setTranslateNamespace(".kart.evgjf");

const ownerManager = useOwner();
const { owner } = storeToRefs(useOwner());

function removePrefixPhoneNumber(phoneNumber: string) {
    return phoneNumber.replace(/^\+[0-9]+\s*/, "0");
}
function getCardClass(index: number) {
    const css: string[] = [`option-card-${index + 1}`];
    if (isSmallerThanXl.value) {
        css.push("my-3");
        css.push("mx-6");
    } else {
        css.push("my-auto");
    }
    return css;
}

const optionPriceAttributeName = "priceWeddingPackOption";
const options = computed(() => {
    const array: OptionCardProps[] = [];
    for (let i = 1; i <= 3; i++) {
        const website = owner.value?.website;
        const data = {
            name: translate(`speed2max.kart.evgjf.options.option${i}.name`).value,
            titleIcon: `/images/kart/wedding-option${i}.svg`,
            title: translate(`speed2max.kart.evgjf.options.option${i}.title2`).value,
            content: translate(`speed2max.kart.evgjf.options.option${i}.content2`).value,
            price: website ? website[`${optionPriceAttributeName}${i}`] : 0,
        };
        array.push(data);
    }
    return array;
});

useSeoMeta({
    title: "EVG EVJF karting électrique | Wedding Kart Race",
    description:
        "EVG ou EVJF original en karting électrique : formule Wedding Kart Race avec Grand Prix, podium festif et option by night. Dès 5 personnes, piste privatisée.",
    path: "/kart/evgjf",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Faire du kart", path: "/kart" },
        { name: "EVG/EVJF", path: "/kart/evgjf" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Wedding Kart Race - EVG/EVJF karting",
            description:
                "EVG ou EVJF original en karting électrique avec Grand Prix, podium festif et options by night.",
            url: "https://speed2max.com/kart/evgjf",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            organizer: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Combien de personnes minimum pour un EVG/EVJF karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule Wedding Kart Race nécessite un minimum de 5 personnes.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Que comprend la formule Wedding Kart Race ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule comprend 3 runs (qualification, demi-finale, finale), un podium festif et une consommation offerte. Des options supplémentaires sont disponibles (by night, karaoké, etc.).",
                    },
                },
                {
                    "@type": "Question",
                    name: "Peut-on organiser un EVG/EVJF en soirée ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Oui, l'option By Night permet de privatiser la piste en soirée pour un EVG ou EVJF encore plus festif.",
                    },
                },
            ],
        },
    ],
});
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <KartNavigation current="evgjf"></KartNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    src="/images/kart/faireDuKart/FDK-evjf-header.jpg"
                    alt="EVG EVJF karting électrique Speed2Max" />
            </template>
            <template #title>
                <span> <Translate path=".title"></Translate></span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitle"></Translate>
                    <span class="has-text-primary">
                        <Translate path=".subtitle2"></Translate>
                    </span>
                </span>
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <Translate path=".content"></Translate>
            </template>
            <template #moreInfos>
                <Translate path=".moreInfos"></Translate>
            </template>
            <template #icon>
                <img src="/images/kart/wedding-icon.png" alt="Icône EVG EVJF karting" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>

        <div class="pack-grid" :class="{ 'mr-6': !isSmallerThanXl }">
            <PackCard
                class="pack-item"
                head-image="/images/kart/wedding-title.svg"
                :price="ownerManager.owner?.website?.priceWeddingPack">
                <template #content>
                    <TranslateNamespace path=".pack">
                        <VFlex
                            flex-direction="column"
                            class="is-flex is-align-self-stretch py-3 is-size-content"
                            align-items="start">
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp; 3</span>
                                <Translate path=".feature1"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 1 </span>
                                <Translate path=".feature11"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 2 </span>
                                <Translate path=".feature12"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 3 </span>
                                <Translate path=".feature13"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature2"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature3"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature4"></Translate>
                            </p>
                        </VFlex>
                    </TranslateNamespace>
                </template>
            </PackCard>
            <OptionCard
                v-for="(opt, index) in options"
                :key="index"
                class="my-auto"
                :name="opt.name"
                :title-icon="opt.titleIcon"
                :title="opt.title"
                :content="opt.content"
                :price="opt.price"
                :class="getCardClass(index)"></OptionCard>
        </div>

        <VFlex class="px-6 py-5 mb-5" flex-direction="column">
            <VFlex v-if="ownerManager.ownerID === null" :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
            <Banner class="m-5 has-gradient-background-dark-ltr w-100 is-weight-600">
                <Translate path=".banner"></Translate><br />
                <a
                    v-if="ownerManager.owner"
                    class="is-weight-900"
                    :href="`tel:${ownerManager.owner?.phoneNumber}`"
                    >{{ removePrefixPhoneNumber(ownerManager.owner?.phoneNumber) }}</a
                >
            </Banner>
        </VFlex>
        <Footer></Footer>
    </VFlex>
</template>

<style scoped lang="scss">
@use "sass:color";

.pack-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    row-gap: 1rem;

    > .pack-item {
        grid-column-start: 1;
        grid-column-end: 3;
        grid-row-start: 1;
        grid-row-end: 4;
    }

    > .option-item-1 {
        grid-column-start: 3;
        grid-column-end: 4;
        grid-row-start: 1;
        grid-row-end: 2;
    }

    > .option-item-2 {
        grid-column-start: 3;
        grid-column-end: 4;
        grid-row-start: 2;
        grid-row-end: 3;
    }

    > .option-item-3 {
        grid-column-start: 3;
        grid-column-end: 4;
        grid-row-start: 3;
        grid-row-end: 4;
    }
}

@media screen and (max-width: 1360px) {
    .pack-grid {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        grid-template-rows: repeat(2, 1fr) repeat(3, 0.6fr);

        > .pack-item {
            grid-column-start: 1 !important;
            grid-column-end: 2 !important;
            grid-row-start: 1 !important;
            grid-row-end: 3 !important;
        }

        > .option-item-1 {
            grid-column-start: 1 !important;
            grid-column-end: 2 !important;
            grid-row-start: 3 !important;
            grid-row-end: 4 !important;
        }

        > .option-item-2 {
            grid-column-start: 1 !important;
            grid-column-end: 2 !important;
            grid-row-start: 4 !important;
            grid-row-end: 5 !important;
        }

        > .option-item-3 {
            grid-column-start: 1 !important;
            grid-column-end: 2 !important;
            grid-row-start: 5 !important;
            grid-row-end: 6 !important;
        }
    }
}

.option-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 1230px) {
    .option-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 767px) {
    .option-grid {
        grid-template-columns: repeat(1, 1fr);
    }
}

.title-evjf {
    max-height: 100px;
}

@media (max-width: 700px) {
    .title-evjf {
        max-height: 65px;
    }
}

.w-100 {
    width: 100%;
}

.pack-block {
    position: relative;
    border-radius: 5px;

    .is-size-content {
        font-size: 1.5rem;
    }

    .top-right-img {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        max-height: 24%;
    }

    @media (max-width: 700px) {
        .top-right-img {
            max-height: 70px;
        }

        .is-size-content {
            font-size: 1.2rem;
        }
    }
}

.banner {
    a {
        &:hover {
            color: white;
        }
    }
}

.option {
    background: radial-gradient(circle, $dark 0%, $blue 100%);
    background-size: 200% 200%;
    animation: gradient 20s ease infinite;

    &:nth-child(1) {
        animation-delay: 0.25s;
    }

    &:nth-child(2) {
        animation-delay: 0.5s;
    }
}

@keyframes gradient {
    0% {
        background-position: 0% 100%;
    }

    50% {
        background-position: 100% 0%;
    }

    100% {
        background-position: 0% 100%;
    }
}
</style>
